import React from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import Download from '@mui/icons-material/Download';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { customerExportsApi } from 'components/customers/export/customerExportsApi';
import { useSnackbar } from 'notistack';
import { MESSAGE_CUSTOMER_EXPORT_FILE_DOWNLOAD_FAIL } from 'config/messages';
import logger from 'lib/logger';
import { ICustomerExportOperation } from 'components/customers/export/models';
import { createDownloadLink } from 'lib/helpers';
import { GridCircularProgress } from 'lib/MuiGrid/GridCircularProgress';

interface ICustomerExportedDataActionCellProps {
    operation: ICustomerExportOperation;
}

export const CustomerExportedDataActionCell: React.FC<ICustomerExportedDataActionCellProps> = ({
    operation
}) => {
    const [loading, setLoading] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const handleButtonClick = React.useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setLoading(true);

            try {
                const response = await customerExportsApi.getCustomerExportFile(operation.result?.href);
                if (!response.ok) {
                    throw new Error(response.statusText);
                }
                const blob = await response.blob();
                createDownloadLink(blob, 'customers.zip');
            } catch (error) {
                logger.error(error);
                enqueueSnackbar(MESSAGE_CUSTOMER_EXPORT_FILE_DOWNLOAD_FAIL, { variant: 'error' });
            } finally {
                setLoading(false);
            }
        },
        [enqueueSnackbar, operation.result?.href]
    );

    const progress = React.useMemo(() => {
        if (operation.result && operation.result.total) {
            return (operation.result.exported + operation.result.errors) / operation.result.total;
        }
        if (operation?.result?.total === 0) {
            return 1;
        }
        return undefined;
    }, [operation.result]);

    return (
        <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
            <GridCircularProgress
                progress={progress !== undefined ? progress * 100 : -1}
                isError={operation.status === OperationStatus.ERROR}
            >
                {operation.status === OperationStatus.DONE && operation.result && operation.result.href ? (
                    <Tooltip title="Click to download">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={handleButtonClick}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} /> : <Download />}
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Box />
                )}
            </GridCircularProgress>
        </Box>
    );
};
